/* eslint-disable react/prop-types */
/* SkillGapDrawer — AI-powered skill-gap + next-role recommendations.
   Slides in from the right inside the Editor. Calls
   POST /api/resumes/{rid}/skill-gap once on open and renders the report. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Lightbulb, Loader2, X, Sparkles, Target, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function SkillGapDrawer({ open, onOpenChange, resumeId }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!open || !resumeId) return;
    setReport(null);
    setBusy(true);
    api.post(`/resumes/${resumeId}/skill-gap`)
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Skill-gap analysis failed"))
      .finally(() => setBusy(false));
  }, [open, resumeId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[460px] flex flex-col gap-0 p-0" data-testid="skill-gap-drawer">
        <SheetHeader className="border-b border-border px-5 py-4 bg-gradient-to-r from-primary/10 to-transparent">
          <SheetTitle className="flex items-center gap-2 display"><Lightbulb className="size-4 text-primary" /> AI Skill-gap report</SheetTitle>
          <p className="text-xs text-muted-foreground">Strengths, gaps, and recommended next roles based on this resume.</p>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {busy && (
            <div className="text-sm text-muted-foreground py-8 text-center inline-flex items-center gap-2 w-full justify-center">
              <Loader2 className="size-4 animate-spin" /> Analysing resume…
            </div>
          )}
          {!busy && report && (
            <>
              {report.summary && (
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-1">Assessment</div>
                  <p className="text-sm">{report.summary}</p>
                </div>
              )}
              {report.strengths?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-2 flex items-center gap-1"><Sparkles className="size-3" /> Strengths</div>
                  <div className="space-y-2" data-testid="skill-gap-strengths">
                    {report.strengths.map((s, i) => (
                      <div key={i} className="rounded-md border border-border p-3 bg-emerald-500/5">
                        <div className="font-bold text-sm">{s.skill}</div>
                        <div className="text-xs text-muted-foreground mt-1">{s.evidence}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {report.gaps?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-amber-600 font-bold mb-2 flex items-center gap-1"><AlertTriangle className="size-3" /> Gaps</div>
                  <div className="space-y-2" data-testid="skill-gap-gaps">
                    {report.gaps.map((g, i) => (
                      <div key={i} className="rounded-md border border-border p-3 bg-amber-500/5">
                        <div className="font-bold text-sm">{g.skill}</div>
                        <div className="text-xs text-muted-foreground mt-1">{g.why_it_matters}</div>
                        {g.how_to_acquire && <div className="text-[11px] text-foreground mt-2 italic">→ {g.how_to_acquire}</div>}
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {report.next_roles?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-2 flex items-center gap-1"><TrendingUp className="size-3" /> Suggested next roles</div>
                  <div className="space-y-2" data-testid="skill-gap-next-roles">
                    {report.next_roles.map((r, i) => (
                      <div key={i} className="rounded-md border border-border p-3 bg-primary/5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-bold text-sm">{r.title}</div>
                          <div className="text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full bg-primary text-primary-foreground">{r.readiness_pct ?? 0}%</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{r.rationale}</div>
                        <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.max(2, r.readiness_pct ?? 0)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {report.ats_tips?.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2 flex items-center gap-1"><Target className="size-3" /> ATS tips</div>
                  <ul className="space-y-1.5">
                    {report.ats_tips.map((t, i) => (
                      <li key={i} className="text-sm flex items-start gap-2"><span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />{t}</li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
          {!busy && !report && (
            <div className="text-sm text-muted-foreground py-10 text-center">No report yet.</div>
          )}
        </div>
        <div className="border-t border-border p-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full" data-testid="skill-gap-close">Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
